import mergeWith from 'lodash/mergeWith';

import { resolveEnterpriseConfig } from '#client/configs/enterprise';
import { ACCOUNT_TYPES } from '#client/constants/accountTypes';
import { ROUTE_ACCESS_POLICIES } from '#client/constants/routeAccessPolicies';

const STANDARD_ACCOUNT_EXPERIENCE = {
  accountType: ACCOUNT_TYPES.STANDARD,
  chrome: {
    businessEntitySelector: {
      showAddNewBusiness: true,
      showViewBusinesses: true,
    },
    showBusinessEntityNavMenu: true,
    showEnterpriseLink: false,
    showVeloAssistant: true,
    showVeloFloatingButton: true,
    userMenuId: ACCOUNT_TYPES.STANDARD,
  },
  footer: {
    showDisclosure: true,
  },
  routing: {
    accessPolicy: ROUTE_ACCESS_POLICIES.STANDARD,
  },
};

const ENTERPRISE_ACCOUNT_EXPERIENCE = {
  accountType: ACCOUNT_TYPES.ENTERPRISE,
  chrome: {
    businessEntitySelector: {
      showAddNewBusiness: false,
      showViewBusinesses: false,
    },
    showBusinessEntityNavMenu: false,
    showEnterpriseLink: true,
    showVeloAssistant: false,
    showVeloFloatingButton: false,
    userMenuId: ACCOUNT_TYPES.ENTERPRISE,
  },
  footer: {
    showDisclosure: false,
  },
  routing: {
    accessPolicy: ROUTE_ACCESS_POLICIES.HOME_ONLY,
  },
};

/**
 * Replaces override arrays during account experience merging instead of merging by index.
 *
 * @param {*} objectValue - Existing merged value.
 * @param {*} sourceValue - Incoming override value.
 * @returns {*|undefined} Override array value, or undefined for default merge behavior.
 */
function replaceArrays(objectValue, sourceValue) {
  if (Array.isArray(sourceValue)) {
    return sourceValue;
  }

  return undefined;
}

/**
 * Merges enterprise-specific account experience overrides onto a base experience.
 *
 * @param {object} baseExperience - Standard or enterprise account experience defaults.
 * @param {object} experienceOverrides - Partial account experience overrides.
 * @returns {object} Merged account experience.
 */
function mergeAccountExperience(baseExperience, experienceOverrides) {
  return mergeWith({}, baseExperience, experienceOverrides, replaceArrays);
}

/**
 * Resolves account experience policy for standard or enterprise dashboard users.
 *
 * @param {object|null|undefined} enterprise - Resolved or minimal enterprise object.
 * @returns {object} Account experience config for chrome and routing.
 */
export function getAccountExperience(enterprise) {
  const resolvedEnterprise = resolveEnterpriseConfig(enterprise);

  if (!resolvedEnterprise) {
    return STANDARD_ACCOUNT_EXPERIENCE;
  }

  return mergeAccountExperience(ENTERPRISE_ACCOUNT_EXPERIENCE, resolvedEnterprise.experienceOverrides ?? {});
}
